/**
 * WeekAgenda (P8 IA cleanup) — read-only 7-day strip + density-aware agenda.
 *
 * Strict reuse of P8 derivations. No new state, no writes, no scheduling.
 *
 * Density rule (locked):
 *   • ≤ 3 items in next 7 days → simple flat list (no day grouping).
 *   • ≥ 4 items → grouped by day with day headers.
 */

import { useMemo } from "react";
import { Calendar as CalendarIcon, MapPin, Clock, DollarSign } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { CommandCenterItem } from "@/data/contractor/commandCenter";

export const AGENDA_DENSITY_THRESHOLD = 4;

function startOfTodayUTC(now: Date): Date {
  return new Date(now.toISOString().slice(0, 10) + "T00:00:00.000Z");
}

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function shortDay(iso: string): string {
  const d = new Date(iso + "T00:00:00.000Z");
  return d.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });
}

function dayNum(iso: string): string {
  return String(parseInt(iso.slice(8, 10), 10));
}

function formatExpiresIn(ms: number): string {
  if (ms <= 0) return "Expired";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

function KindDot({ kind }: { kind: CommandCenterItem["kind"] }) {
  const cls: Record<CommandCenterItem["kind"], string> = {
    assigned_scheduled: "bg-info",
    assigned_unscheduled: "bg-warning",
    pending_offer: "bg-primary",
    opportunity: "bg-success",
  };
  return <span className={`h-1.5 w-1.5 rounded-full ${cls[kind]}`} aria-hidden />;
}

function KindLabel({ kind }: { kind: CommandCenterItem["kind"] }) {
  const map: Record<CommandCenterItem["kind"], { label: string; cls: string }> = {
    assigned_scheduled: { label: "Assigned", cls: "bg-info/10 text-info border-info/20" },
    assigned_unscheduled: { label: "Schedule needed", cls: "bg-warning/10 text-warning border-warning/30" },
    pending_offer: { label: "Pending offer", cls: "bg-primary/15 text-primary border-primary/30" },
    opportunity: { label: "Opportunity", cls: "bg-success/10 text-success border-success/20" },
  };
  const v = map[kind];
  return (
    <Badge variant="outline" className={`text-[10px] ${v.cls}`}>
      {v.label}
    </Badge>
  );
}

function AgendaRow({
  it,
  onClick,
}: {
  it: CommandCenterItem;
  onClick?: (it: CommandCenterItem) => void;
}) {
  const clickable = !!onClick;
  return (
    <li
      data-testid="agenda-item"
      data-kind={it.kind}
      data-job-id={it.jobId}
      className={`rounded-lg border border-border/60 bg-background/40 p-2.5 ${
        clickable
          ? "cursor-pointer hover:bg-muted/40 focus-within:ring-1 focus-within:ring-primary/30 transition-colors"
          : ""
      }`}
      onClick={clickable ? () => onClick!(it) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick!(it);
              }
            }
          : undefined
      }
    >
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <KindDot kind={it.kind} />
        <KindLabel kind={it.kind} />
        <span className="text-[10px] font-mono text-muted-foreground ml-auto">
          {it.jobLabel}
        </span>
      </div>
      <p className="text-xs font-semibold truncate">{it.title}</p>
      <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground flex-wrap">
        {it.city && (
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {it.city}
          </span>
        )}
        {it.kind === "pending_offer" && it.expiresInMs != null && (
          <span
            data-testid="offer-countdown"
            className="flex items-center gap-1 text-warning font-medium"
          >
            <Clock className="h-3 w-3" /> {formatExpiresIn(it.expiresInMs)}
          </span>
        )}
        {it.payoutAmount != null && (
          <span className="flex items-center gap-1 font-medium text-foreground">
            <DollarSign className="h-3 w-3" />
            {it.payoutAmount.toLocaleString()}
          </span>
        )}
      </div>
    </li>
  );
}

interface WeekAgendaProps {
  items: CommandCenterItem[];
  now?: Date;
  /** Optional click handler — when present, agenda rows become clickable. */
  onItemClick?: (item: CommandCenterItem) => void;
}

export function WeekAgenda({
  items,
  now = new Date(),
  onItemClick,
}: WeekAgendaProps) {
  const today = startOfTodayUTC(now);
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today);
      d.setUTCDate(today.getUTCDate() + i);
      return isoDay(d);
    });
  }, [today]);

  const sevenDayEnd = weekDays[weekDays.length - 1];

  // In-window dated items (today..+6 days), and undated assigned (always shown).
  const dated = items.filter(
    (i) => i.date && i.date >= weekDays[0] && i.date <= sevenDayEnd,
  );
  const undated = items.filter(
    (i) => !i.date && i.kind === "assigned_unscheduled",
  );
  const inWindow = [...dated, ...undated];

  const countsByDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const day of weekDays) m.set(day, 0);
    for (const it of dated) {
      if (it.date) m.set(it.date, (m.get(it.date) ?? 0) + 1);
    }
    return m;
  }, [weekDays, dated]);

  const totalInWindow = inWindow.length;
  const dense = totalInWindow >= AGENDA_DENSITY_THRESHOLD;

  return (
    <div data-testid="week-agenda" data-density={dense ? "grouped" : "list"}>
      {/* 7-day strip */}
      <div className="flex items-center justify-between mb-2 px-0.5">
        <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1.5">
          <CalendarIcon className="h-3.5 w-3.5" /> This Week
        </h3>
        <span className="text-[10px] text-muted-foreground">
          {totalInWindow} item{totalInWindow === 1 ? "" : "s"}
        </span>
      </div>
      <div
        data-testid="week-strip"
        className="grid grid-cols-7 gap-1 mb-3"
      >
        {weekDays.map((day) => {
          const count = countsByDay.get(day) ?? 0;
          const isToday = day === weekDays[0];
          return (
            <div
              key={day}
              data-testid="week-strip-day"
              data-date={day}
              data-count={count}
              className={`flex flex-col items-center rounded-md py-1.5 text-[10px] ${
                isToday
                  ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                  : "bg-muted/30 text-muted-foreground"
              }`}
            >
              <span className="uppercase tracking-wide">{shortDay(day)}</span>
              <span className="text-sm font-bold leading-tight">{dayNum(day)}</span>
              {count > 0 && (
                <span className="mt-0.5 inline-flex h-1 w-1 rounded-full bg-current" aria-hidden />
              )}
            </div>
          );
        })}
      </div>

      {/* Agenda — density-aware */}
      {totalInWindow === 0 ? (
        <p className="text-xs text-muted-foreground py-3 text-center">
          Nothing on your calendar this week.
        </p>
      ) : dense ? (
        <div className="space-y-3" data-testid="agenda-grouped">
          {weekDays.map((day) => {
            const dayItems = dated.filter((i) => i.date === day);
            if (dayItems.length === 0) return null;
            return (
              <section key={day} data-testid="agenda-day-group" data-date={day}>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  {shortDay(day)} · {day.slice(5)}
                </p>
                <ul className="space-y-1.5">
                  {dayItems.map((it) => (
                    <AgendaRow key={`${it.kind}-${it.jobId}`} it={it} onClick={onItemClick} />
                  ))}
                </ul>
              </section>
            );
          })}
          {undated.length > 0 && (
            <section data-testid="agenda-undated-group">
              <p className="text-[10px] font-semibold text-warning uppercase tracking-wider mb-1.5">
                Schedule needed
              </p>
              <ul className="space-y-1.5">
                {undated.map((it) => (
                  <AgendaRow key={`${it.kind}-${it.jobId}`} it={it} onClick={onItemClick} />
                ))}
              </ul>
            </section>
          )}
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="agenda-flat">
          {inWindow.map((it) => (
            <AgendaRow key={`${it.kind}-${it.jobId}`} it={it} onClick={onItemClick} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default WeekAgenda;
